import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { allArticles, categories } from '@/mocks/articles';
import { getArticleContent } from '@/mocks/articleContents';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const content = slug ? getArticleContent(slug) : null;
  const category = categories.find((c) => c.slug === article?.category);
  const relatedArticles = allArticles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 3);

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，你要找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} | 悦己新知`;
  const pageDesc = article.excerpt.slice(0, 150);

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${article.title},自慰喷水,女性潮喷原理,${category?.name || '性健康科普'}`}
        canonicalUrl={`/article/${slug}`}
        pageType="Article"
        publishDate={article.date}
        articleCategory={category?.name}
        articleImage={article.image}
        authorName={content.author}
      />
      <Navbar />

      <main className="pt-16">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            {category && (
              <>
                <Link to={`/category/${category.slug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </>
            )}
            <span className="text-foreground-700 line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {content.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} 阅读
              </span>
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors"
                >
                  <i className={`${category.icon} text-xs`}></i>
                  {category.name}
                </Link>
              )}
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover object-top"
            />
          </div>

          <div
            className="prose-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />

          <div className="mt-12 p-6 rounded-xl bg-background-100 border border-background-200/70">
            <p className="text-sm text-foreground-600 leading-relaxed">
              <strong>免责声明：</strong>本文内容仅供科普参考，不能替代专业医疗建议。如有任何身体健康方面的疑虑，请及时咨询专业医生。每个人的身体状况不同，请以安全和舒适为首要原则。
            </p>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-3xl mx-auto px-4 md:px-6 pb-14">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all cursor-pointer"
                >
                  <img
                    src={rel.image}
                    alt={rel.title}
                    className="w-full h-36 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}